import React, { FC, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { CustomerStepField } from "./steps";
import { stateGstCodeMap } from "../../constants/stateList";
import ControlledTextField from "../../common/ControlledTextField";
import { useFormContext, Control, Controller } from "react-hook-form";
import CustomDropdown from "../../common/CustomDropdown";
import {
  documentLabelStyle,
  fieldTitleStyle,
  iconButtonstyle,
  radioIconStyle,
  selectedImageDiv,
  titleStyle,
  uploadButton,
  uploadButtonContainer,
  getDropdownCreateButtonStyle,
  dropdownCreateLabelStyle,
  dropdownCreateOptionStyle,
  dropdownCreateMenuStyle,
} from "./customerStepStyles";
import CloseIcon from "@mui/icons-material/Close";
import FilePreviewDialog from "../../common/FilePreviewDialog";
import colors from "../../theme/Colours";

type CustomerStepProps = {
  title: string;
  fields: CustomerStepField[];
  control: Control<any>;
  disabled?: boolean;
  isFirstTitle?: boolean;
  isCreateNewCustomer?: boolean;
};

export const CustomerStep: FC<CustomerStepProps> = ({
  control,
  title,
  fields,
  disabled = false,
  isFirstTitle = false,
  isCreateNewCustomer = false,
}) => {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext();

  const uploadVariables = fields
    .filter((field) => field.type === "file")
    .map((field) => field.name);

  uploadVariables.forEach((variable) => {
    register(variable);
  });

  const values = watch();

  const [selectedField, setSelectedField] = useState<string | null>(null);

  const [previewFile, setPreviewFile] = useState<File>();
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string>();

  const imagesLength = Object.keys(values).filter(
    (key) => values[key] instanceof File || values[key].url
  ).length;

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !selectedField) {
      // If no file is selected or no field is selected, just return
      setSelectedField(null);
      return;
    }

    const file = e.target.files[0];

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    // Check for allowed file types
    if (!allowedTypes.includes(file.type)) {
      setFileError("Only PDF, JPG, and PNG files are allowed.");
      return;
    }

    setFileError(null);
    setValue(selectedField, file);
    setSelectedField(null);
  };

  const handleRemoveFile = (fieldName: string) => {
    if (
      fields.find((field: CustomerStepField) => field.name === fieldName)
        ?.required === undefined
    )
      // Resets the value to empty string instead of object to prevent required errors
      setValue(fieldName, "");
    else setValue(fieldName, {});
    setSelectedField(null);
  };

  const uploadErrors = uploadVariables.map((variable) => errors[variable]);

  const [openPreview, setOpenPreview] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [urlFileType, setUrlFileType] = useState("");

  return (
    <>
      <div>
        <Typography sx={{ ...titleStyle, mt: isFirstTitle ? "0px" : "60px" }}>
          {title}
        </Typography>

        {/**
         *  Used to generate the fields based on the template
         *  Made this template driven incase we have more fields later
         */}
        <Grid2 container rowSpacing={[0, "16px"]} columnSpacing={[0, "36px"]}>
          {fields.map((field) => {
            if (field.type === "file") return;
            if (field.type === "file") return null;

            if (field.type === "dropdown") {
              const dropdownStyles = {
                button: {
                  ...getDropdownCreateButtonStyle(disabled),
                },
                label: dropdownCreateLabelStyle,
                menu: dropdownCreateMenuStyle,
                option: dropdownCreateOptionStyle,
                menuClass: "customer-page-dropdown",
              };
              return (
                <Grid2 key={field.name} size={[12, 6, 4]}>
                  <Typography sx={fieldTitleStyle}>
                    {field.title}
                    {field.required}
                  </Typography>
                  <Controller
                    name={field.name}
                    control={control}
                    render={({ field: rhfField, fieldState }) => (
                      <>
                        <CustomDropdown
                          label={`Select`}
                          value={rhfField.value || ""}
                          onChange={(value) => {
                            rhfField.onChange(value);
                            if (field.name === "state") {
                              const code = stateGstCodeMap[value as string];
                              if (code) setValue("gstStateCode", code);
                            }
                          }}
                          options={field.options || []}
                          disabled={disabled}
                          customStyles={dropdownStyles}
                        />
                        {fieldState.error && (
                          <Typography color="error" fontSize="12px" mt="4px">
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Grid2>
              );
            }

            return (
              <Grid2
                key={field.name}
                size={field?.type === "textarea" ? 12 : [12, 6, 4]}
              >
                <Typography sx={fieldTitleStyle}>
                  {field.title}
                  {field.required && "*"}
                </Typography>
                <ControlledTextField
                  key={field.name}
                  control={control}
                  name={field.name}
                  placeholder={field.placeholder ?? ""}
                  disabled={disabled}
                  readOnly={field.name === "emailID" && !isCreateNewCustomer}
                  multiline={field?.type === "textarea"}
                  inputProps={
                    field.maxLength ? { maxLength: field.maxLength } : undefined
                  }
                  onlyDigits={!!field.onlyDigits}
                  sx={{
                    ...(field.name === "emailID" &&
                      !isCreateNewCustomer && {
                        backgroundColor: "#f5f5f5",
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "rgba(0, 0, 0, 0.23) !important",
                          },
                        "& .MuiInputBase-input": {
                          cursor: "not-allowed",
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                        },
                      }),
                  }}
                />
              </Grid2>
            );
          })}
        </Grid2>

        {/**
         * Same as above but for file upload
         */}
        <Typography sx={fieldTitleStyle} mt={"16px"}>
          Upload Documents
        </Typography>
        <RadioGroup
          value={selectedField}
          onChange={(e) => setSelectedField(e.target.value)}
        >
          {fields.map((field) => {
            if (field.type === "file")
              return (
                <FormControlLabel
                  key={field.name}
                  control={
                    <Radio
                      sx={radioIconStyle}
                      checked={
                        selectedField === field.name ||
                        !!values[field.name].name
                      }
                      disabled={!!values[field.name].name || disabled}
                    />
                  }
                  label={`${field.title}${field.required ? "*" : ""}`}
                  value={field.name}
                  sx={documentLabelStyle}
                />
              );
          })}
        </RadioGroup>

        {/**
         *  Display the selected files
         */}
        <Box display={"flex"} flexWrap={"wrap"} mb={"14px"} gap={2}>
          {Object.keys(values)
            .filter((fieldName) => uploadVariables.includes(fieldName))
            .map((fieldName) => {
              if (values[fieldName].type || values[fieldName].url)
                return (
                  <Box
                    key={fieldName}
                    sx={selectedImageDiv}
                    onClick={() => {
                      if (values[fieldName].url) {
                        setUrlFileType(values[fieldName]?.fileType);
                        setPreviewFileUrl(values[fieldName].url);
                      } else setPreviewFile(values[fieldName]);
                      setOpenPreview(true);
                    }}
                  >
                    <Typography
                      sx={[
                        fieldTitleStyle,
                        { marginBottom: 0, color: colors.buttonBGColor },
                      ]}
                    >
                      {values[fieldName].name}
                    </Typography>
                    {!disabled && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(fieldName);
                        }}
                        sx={iconButtonstyle}
                      >
                        <CloseIcon
                          sx={{ fontSize: 18, color: colors.buttonBGColor }}
                        />
                      </IconButton>
                    )}
                  </Box>
                );
            })}
        </Box>

        {!disabled && (
          <>
            <input
              type="file"
              disabled={!selectedField}
              id={`${title}UploadButton`}
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            {selectedField && (
              <label htmlFor={`${title}UploadButton`}>
                <div style={uploadButtonContainer}>
                  <div style={uploadButton}>Choose file</div>
                  {imagesLength === 0 ? "No" : imagesLength} file
                  {imagesLength > 1 && "s"} chosen
                </div>
              </label>
            )}
            {/* To display ErrMsg if unsupported file is chosen */}
            {fileError && <Typography color="error">{fileError}</Typography>}
          </>
        )}

        {uploadErrors.some((error) => error?.message) && (
          <Typography color={"error"}>Upload required files</Typography>
        )}
      </div>

      <FilePreviewDialog
        open={openPreview}
        handleClose={() => setOpenPreview(false)}
        setDownloadUrl={setDownloadUrl}
        downloadUrl={downloadUrl}
        previewFile={previewFile}
        previewFileUrl={previewFileUrl}
        contentType={urlFileType}
      />
    </>
  );
};
